// src/components/MuultiNavbar.js
import React, { useEffect, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { Link, useLocation } from 'react-router-dom';
import { db } from '../firebaseConfig';
import useAdminSession from '../hooks/useAdminSession';
import EmailWalletButton from './EmailWalletButton';

import './MuultiStyle.css';
import './MuultiNavbar.css';

const THEME_STORAGE_KEY = "muulti-theme-preference";

function readThemePreference() {
    try {
        const raw = localStorage.getItem(THEME_STORAGE_KEY);
        if (raw === "light" || raw === "dark") return raw;
    } catch {}
    return null;
}

function resolveThemeFromDocument() {
    const stored = readThemePreference();
    if (stored) return stored;
    if (typeof document === "undefined") return "light";
    const body = document.body;
    if (body?.classList?.contains("theme-dark") || body?.classList?.contains("public-theme-dark")) {
        return "dark";
    }
    return "light";
}

export default function MuultiNavbar() {
    const wallet = useWallet();
    const { publicKey, connected } = wallet;
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [themeMode, setThemeMode] = useState(() => resolveThemeFromDocument());
    const { authorized: isAdmin } = useAdminSession(wallet);

    useEffect(() => {
        setUsername('');
        if (!connected || !publicKey) return;
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('publicKey', '==', publicKey.toString()));
        return onSnapshot(q, (snap) => {
            setUsername(snap.docs[0]?.data()?.muultiName || '');
        }, (e) => {
            console.error('Navbar username fetch error:', e);
        });
    }, [connected, publicKey]);

    // keep your width var hook (unchanged)
    useEffect(() => {
        const setWidthVar = () => {
            const sw = document.documentElement.scrollWidth || document.body.scrollWidth;
            document.documentElement.style.setProperty('--page-scroll-width', sw + 'px');
        };
        setWidthVar();
        window.addEventListener('resize', setWidthVar);
        return () => window.removeEventListener('resize', setWidthVar);
    }, []);

    useEffect(() => {
        const closeOnResize = () => {
            if (window.innerWidth > 1024) {
                setMenuOpen(false);
            }
        };
        window.addEventListener('resize', closeOnResize);
        return () => window.removeEventListener('resize', closeOnResize);
    }, []);

    useEffect(() => {
        const handleThemeChange = () => setThemeMode(resolveThemeFromDocument());
        handleThemeChange();
        window.addEventListener("muulti-theme-change", handleThemeChange);
        window.addEventListener("storage", handleThemeChange);
        return () => {
            window.removeEventListener("muulti-theme-change", handleThemeChange);
            window.removeEventListener("storage", handleThemeChange);
        };
    }, []);

    useEffect(() => {
        setThemeMode(resolveThemeFromDocument());
    }, [location.pathname]);

    const handleNavClick = () => {
        if (menuOpen) setMenuOpen(false);
    };

    const handleThemeToggle = () => {
        const next = themeMode === "dark" ? "light" : "dark";
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {}
        window.dispatchEvent(new Event("muulti-theme-change"));
        setThemeMode(next);
    };

    return (
        <>
        <nav className="navbar">
            <div className="navbar-left">
                <div className="logo-container">
                <Link to='/' onClick={handleNavClick}>
                <img id="navbar-image" src="/muulti_logo_one.png" alt="Muulti Logo" />
                </Link>
                </div>
            </div>

            <ul
                id="muulti-navbar-links"
                className={`navbar-links ${menuOpen ? 'is-open' : ''}`}
            >
                {/* Your original destinations, unchanged */}
                <li className="nav-item">
                    <Link
                        to={
                            username
                                ? `/${username}`
                                : '/muulti-directory'
                        }
                        className="nav-link"
                        onClick={handleNavClick}
                    >
                        {username ? 'Preview Space' : 'Preview'}
                    </Link>
                </li>

                <li className="nav-item">
                    <Link to="/" className="nav-link" onClick={handleNavClick}>Muulti Editor</Link>
                </li>

                <li className="nav-item">
                <Link to="/muulti-directory" className="nav-link" onClick={handleNavClick}>Directory</Link>
            </li>
                <li className="nav-item">
                    <Link to="3d/street" className="nav-link" onClick={handleNavClick}>3D Spaces</Link>
                </li>
                <li className="nav-item">
                    <Link to="/posts" className="nav-link" onClick={handleNavClick}>
                        Social Feed
                    </Link>
                </li>
                {isAdmin && (
                    <li className="nav-item">
                        <Link to="/muultispace-admin" className="nav-link" onClick={handleNavClick}>
                            Admin
                        </Link>
                    </li>
                )}
                {/*<li className="nav-item dropdown">*/}
                {/*    <button className="nav-link dropdown-toggle" aria-haspopup="true" aria-expanded="false">*/}
                {/*        Ads*/}
                {/*    </button>*/}
                {/*    <ul className="dropdown-menu" role="menu">*/}
                {/*        <li role="none">*/}
                {/*            <Link role="menuitem" to="/ads/compose" className="dropdown-item" onClick={handleNavClick}>*/}
                {/*                Ad Composer*/}
                {/*            </Link>*/}
                {/*        </li>*/}
                {/*        <li role="none">*/}
                {/*            <Link role="menuitem" to="/ads/market" className="dropdown-item" onClick={handleNavClick}>*/}
                {/*                Creator Ad Market*/}
                {/*            </Link>*/}
                {/*        </li>*/}
                {/*    </ul>*/}
                {/*</li>*/}
                <li className="nav-item dropdown">
                    <button className="nav-link dropdown-toggle" aria-haspopup="true" aria-expanded="false">
                        Blockchain Tools
                    </button>
                    <ul className="dropdown-menu" role="menu">
                        <li role="none">
                            <a role="menuitem" href="https://explorer.thesimulacrum.xyz/"
                               className="dropdown-item"
                               target="_blank"
                               rel="noopener noreferrer"
                               onClick={handleNavClick}
                            >
                                NFT Wallet Explorer
                            </a>
                        </li>
                        <li role="none">
                            <Link role="menuitem" to="/token-gates" className="dropdown-item" onClick={handleNavClick}>
                                Create Token Gate
                            </Link>
                        </li>
                        <li role="none">
                            <Link role="menuitem" to="/collection-creation" className="dropdown-item" onClick={handleNavClick}>
                                Create Collection
                            </Link>
                        </li>
                        <li role="none">
                            <Link role="menuitem" to="/nft-studio" className="dropdown-item" onClick={handleNavClick}>
                                Mint Single NFT
                            </Link>
                        </li>
                    </ul>
                </li>

                <li className="nav-item">
                    <Link to="/about" className="nav-link" onClick={handleNavClick}>About</Link>
                </li>

            </ul>

            <div className="navbar-wallet">
                <button
                    type="button"
                    className={`theme-toggle ${themeMode === "dark" ? "is-dark" : "is-light"}`}
                    onClick={handleThemeToggle}
                    aria-pressed={themeMode === "dark"}
                    title="Theme toggle applies to supported pages."
                >
                    <span className="theme-toggle__track">
                        <span className="theme-toggle__thumb" />
                    </span>
                    <span className="theme-toggle__text">
                        {themeMode === "dark" ? "Dark" : "Light"}
                    </span>
                </button>
                <EmailWalletButton />
                <div className="wallet-item">
                    <WalletMultiButton />
                </div>
            </div>

            <button
                className="navbar-toggle"
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Toggle navigation"
                aria-expanded={menuOpen}
                aria-controls="muulti-navbar-links"
            >
                <span className="navbar-toggle-bar" />
                <span className="navbar-toggle-bar" />
                <span className="navbar-toggle-bar" />
            </button>
        </nav>
        {/*<div className="maintenance-banner" role="status" aria-live="polite">*/}
        {/*    <span className="maintenance-banner__label">Maintenance</span>*/}
        {/*    <span>MuultiSpace is undergoing maintenance. Some features are currently offline.</span>*/}
        {/*</div>*/}
        </>
    );
}
